import { request } from './http'

export type StorylineStatus = 'active' | 'dormant' | 'closed' | string
export type SubplotStatus = 'dug' | 'filling' | 'detonated' | string

export interface Storyline {
  id: string
  project_id: string
  name: string
  line_type: string
  priority: number
  arc_summary: string | null
  status: StorylineStatus
  created_at: number
  updated_at: number
}

export interface StorylineInput {
  name?: string
  line_type?: string
  priority?: number
  arc_summary?: string | null
  status?: StorylineStatus
}

export interface Subplot {
  id: string
  project_id: string
  storyline_id: string | null
  title: string
  description: string | null
  status: SubplotStatus
  dig_chapter_number: number | null
  target_detonate_chapter_number: number | null
  actual_detonate_chapter_number: number | null
  convergence_chapter_number: number | null
  importance: number
  remind_before_chapters: number
  include_in_context: boolean
  created_at: number
  updated_at: number
  detonated_at: number | null
}

export interface SubplotInput {
  storyline_id?: string | null
  title?: string
  description?: string | null
  dig_chapter_number?: number | null
  target_detonate_chapter_number?: number | null
  convergence_chapter_number?: number | null
  importance?: number
  remind_before_chapters?: number
  include_in_context?: boolean
}

export interface ChapterStorylineWeight {
  id: string
  project_id: string
  chapter_number: number
  storyline_id: string
  weight: number
  is_convergence_node: boolean
  updated_at: number
}

export interface ChapterStorylineWeightInput {
  storyline_id: string
  weight: number
  is_convergence_node?: boolean
}

export interface WeightValidation {
  sum: number
  is_near_one: boolean
  warnings: string[]
}

export interface ReplaceWeightsResponse {
  weights: ChapterStorylineWeight[]
  validation: WeightValidation
}

export interface ConvergenceDensity {
  chapter: number
  window: number
  density: number
}

const projectPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/storylines`
const storylinePath = (projectId: string, storylineId: string) =>
  `${projectPath(projectId)}/${encodeURIComponent(storylineId)}`
const subplotPath = (projectId: string) => `${projectPath(projectId)}/subplots`
const subplotItemPath = (projectId: string, subplotId: string) =>
  `${subplotPath(projectId)}/${encodeURIComponent(subplotId)}`
const chapterParam = (chapter: number) => `chapter=${encodeURIComponent(String(chapter))}`

export const subplotApi = {
  listStorylines: async (projectId: string): Promise<Storyline[]> => {
    const response = await request<{ storylines: Storyline[] }>(projectPath(projectId))
    return response.storylines
  },

  createStoryline: (projectId: string, body: StorylineInput): Promise<Storyline> =>
    request<Storyline>(projectPath(projectId), { method: 'POST', body }),

  updateStoryline: (projectId: string, storylineId: string, body: StorylineInput): Promise<Storyline> =>
    request<Storyline>(storylinePath(projectId, storylineId), { method: 'PUT', body }),

  deleteStoryline: (projectId: string, storylineId: string): Promise<void> =>
    request<void>(storylinePath(projectId, storylineId), { method: 'DELETE' }),

  listSubplots: async (projectId: string, storylineId?: string | null): Promise<Subplot[]> => {
    const params = new URLSearchParams()
    if (storylineId) params.set('storyline_id', storylineId)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const response = await request<{ subplots: Subplot[] }>(`${subplotPath(projectId)}${suffix}`)
    return response.subplots
  },

  createSubplot: (projectId: string, body: SubplotInput): Promise<Subplot> =>
    request<Subplot>(subplotPath(projectId), { method: 'POST', body }),

  updateSubplot: (projectId: string, subplotId: string, body: SubplotInput): Promise<Subplot> =>
    request<Subplot>(subplotItemPath(projectId, subplotId), { method: 'PUT', body }),

  deleteSubplot: (projectId: string, subplotId: string): Promise<void> =>
    request<void>(subplotItemPath(projectId, subplotId), { method: 'DELETE' }),

  fillSubplot: (projectId: string, subplotId: string): Promise<Subplot> =>
    request<Subplot>(`${subplotItemPath(projectId, subplotId)}/fill`, { method: 'POST', body: {} }),

  detonateSubplot: (projectId: string, subplotId: string, detonateChapterNumber: number): Promise<Subplot> =>
    request<Subplot>(`${subplotItemPath(projectId, subplotId)}/detonate`, {
      method: 'POST',
      body: { detonate_chapter_number: detonateChapterNumber },
    }),

  listWeights: async (projectId: string, chapter: number): Promise<ChapterStorylineWeight[]> => {
    const response = await request<{ weights: ChapterStorylineWeight[] }>(
      `${projectPath(projectId)}/weights?${chapterParam(chapter)}`,
    )
    return response.weights
  },

  replaceWeights: (
    projectId: string,
    chapter: number,
    body: ChapterStorylineWeightInput[],
  ): Promise<ReplaceWeightsResponse> =>
    request<ReplaceWeightsResponse>(`${projectPath(projectId)}/weights?${chapterParam(chapter)}`, {
      method: 'PUT',
      body,
    }),

  convergenceDensity: (projectId: string, chapter: number, window = 1): Promise<ConvergenceDensity> =>
    request<ConvergenceDensity>(
      `${projectPath(projectId)}/convergence/density?${chapterParam(chapter)}&window=${encodeURIComponent(String(window))}`,
    ),

  convergenceNodes: async (projectId: string): Promise<ChapterStorylineWeight[]> => {
    const response = await request<{ nodes: ChapterStorylineWeight[] }>(`${projectPath(projectId)}/convergence/nodes`)
    return response.nodes
  },
}
